import React from 'react';
import {motion} from 'framer-motion';
import {ArrowLeft, Shield} from 'lucide-react';
import {Link} from 'react-router-dom';
import {useSeo} from '../hooks/useSeo';

export function PrivacyPage() {
    useSeo({
        title: 'Polityka prywatności | Kamery Mielec - kamerymielec.pl',
        description:
            'Polityka prywatności serwisu kamerymielec.pl: administrator danych, zakres przetwarzania, cele, podstawy prawne i Twoje prawa zgodnie z RODO.',
        canonicalPath: '/polityka-prywatnosci',
        robots: 'noindex, follow',
        breadcrumbs: [
            {name: 'Strona główna', path: '/'},
            {name: 'Polityka prywatności', path: '/polityka-prywatnosci'},
        ],
    });

    return (
        <div className="min-h-screen pt-28 pb-20 px-5">
            <div className="max-w-3xl mx-auto">
                <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.5}}>
                    <Link to="/"
                          className="inline-flex items-center gap-2 text-sm text-primary font-medium mb-8 hover:underline">
                        <ArrowLeft size={16}/> Powrót do strony głównej
                    </Link>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                            <Shield size={24} className="text-primary"/>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-extrabold text-foreground">Polityka Prywatności</h1>
                    </div>

                    <p className="text-sm text-muted-foreground mb-8">Ostatnia aktualizacja: 31 marca 2026</p>

                    <div
                        className="prose prose-sm dark:prose-invert max-w-none space-y-6 text-muted-foreground leading-relaxed">
                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">1. Administrator danych</h2>
                            <p>Administratorem Twoich danych osobowych jest ONEXO.pl Adrian Sokół NIP: 8172211997 z
                                siedzibą przy ul. Wojska Polskiego 9/D211, 39-300 Mielec. Kontakt: <a
                                    href="mailto:a.sokol@onexo.pl" className="text-primary">a.sokol@onexo.pl</a>, tel.
                                +48 664 896 662.</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">2. Jakie dane zbieramy</h2>
                            <p>Zbieramy następujące rodzaje danych:</p>
                            <ul className="list-disc pl-5 space-y-1 mt-2">
                                <li>Dane kontaktowe (imię, e-mail, numer telefonu), gdy kontaktujesz się z nami</li>
                                <li>Dane techniczne (adres IP, typ przeglądarki, system operacyjny), automatycznie</li>
                                <li>Pliki cookies, zgodnie z sekcją 5 niniejszej polityki</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">3. Cel przetwarzania danych</h2>
                            <p>Dane przetwarzamy w celu:</p>
                            <ul className="list-disc pl-5 space-y-1 mt-2">
                                <li>Realizacji zapytań i umów o świadczenie usług</li>
                                <li>Kontaktu zwrotnego w odpowiedzi na zapytania</li>
                                <li>Analizy statystycznej ruchu na stronie</li>
                                <li>Wypełnienia obowiązków prawnych</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">4. Podstawa prawna</h2>
                            <p>Przetwarzanie danych odbywa się na podstawie art. 6 ust. 1 RODO: zgoda osoby (lit. a),
                                wykonanie umowy (lit. b), obowiązek prawny (lit. c) lub prawnie uzasadniony interes
                                administratora (lit. f).</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">5. Pliki cookies</h2>
                            <p>Nasza strona wykorzystuje pliki cookies w celu:</p>
                            <ul className="list-disc pl-5 space-y-1 mt-2">
                                <li><strong>Niezbędne</strong>, zapewniają prawidłowe działanie strony (np. zapamiętanie
                                    preferencji motywu)
                                </li>
                                <li><strong>Analityczne</strong>, pomagają nam zrozumieć, jak użytkownicy korzystają ze
                                    strony
                                </li>
                            </ul>
                            <p className="mt-2">Możesz zarządzać plikami cookies w ustawieniach przeglądarki. Usunięcie
                                cookies może wpłynąć na funkcjonalność strony.</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">6. Okres przechowywania</h2>
                            <p>Dane przechowujemy przez okres niezbędny do realizacji celów przetwarzania, nie dłużej
                                niż wymagają tego przepisy prawa.</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">7. Twoje prawa</h2>
                            <p>Przysługuje Ci prawo do:</p>
                            <ul className="list-disc pl-5 space-y-1 mt-2">
                                <li>Dostępu do swoich danych</li>
                                <li>Sprostowania danych</li>
                                <li>Usunięcia danych („prawo do bycia zapomnianym")</li>
                                <li>Ograniczenia przetwarzania</li>
                                <li>Przenoszenia danych</li>
                                <li>Wniesienia sprzeciwu wobec przetwarzania</li>
                                <li>Cofnięcia zgody w dowolnym momencie</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-bold text-foreground mt-8 mb-3">8. Kontakt</h2>
                            <p>W sprawach związanych z ochroną danych osobowych prosimy o kontakt: <a
                                href="mailto:a.sokol@onexo.pl" className="text-primary">a.sokol@onexo.pl</a>.</p>
                        </section>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
